import { useMemo, useState } from "react";
import { ArrowLeft, Clock, Film, Menu, Send, Star } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api.js";
import { EmptyState, Loading, Poster, RatingStars, ReviewCard, Segmented, Surface, WatchlistButton } from "../components.jsx";
import { useAsync } from "../hooks.js";

export default function MoviePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => api(`/v1/movies/${id}`), [id]);
  const [rating, setRating] = useState(3.5);
  const [text, setText] = useState("");
  const [spoilers, setSpoilers] = useState(false);
  const [sort, setSort] = useState("recent");
  const [working, setWorking] = useState(false);
  const [formError, setFormError] = useState("");
  const reviews = useMemo(() => {
    const items = data?.reviews ? [...data.reviews] : [];
    return sort === "likes" ? items.sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0)) : items;
  }, [data?.reviews, sort]);

  const publish = async (event) => {
    event.preventDefault(); setWorking(true); setFormError("");
    try {
      await api(`/v1/movies/${id}/review`, { method: "POST", body: { rating, text, containsSpoilers: spoilers } });
      setText(""); setSpoilers(false); await reload();
    } catch (caught) { setFormError(caught.message); }
    finally { setWorking(false); }
  };

  if (loading) return <div className="page"><Loading label="Abriendo la película…" /></div>;
  if (error || !data) return <div className="page"><EmptyState title="No se ha podido abrir" text={error} /></div>;
  const { movie } = data;
  return (
    <div className="movie-page">
      <div className="movie-backdrop" style={movie.backdropURL ? { backgroundImage: `linear-gradient(180deg, rgba(3,8,6,.16), #030806 94%), url(${movie.backdropURL})` } : undefined} />
      <div className="page movie-content">
        <button className="back-button" onClick={() => navigate(-1)}><ArrowLeft />Volver</button>
        <section className="movie-hero">
          <Poster movie={movie} />
          <div className="movie-meta"><span className="title-mark" /><h1>{movie.title}</h1>{movie.originalTitle && movie.originalTitle !== movie.title ? <p className="original-title">{movie.originalTitle}</p> : null}<div className="meta-line"><span>{movie.year || "—"}</span>{movie.runtime ? <span><Clock />{movie.runtime} min</span> : null}{movie.genres?.slice(0, 3).map((genre) => <span key={genre}>{genre}</span>)}</div>{movie.averageRating ? <div className="aggregate"><RatingStars value={movie.averageRating} size={23} /><strong>{movie.averageRating.toFixed(1)}</strong><span>{movie.reviewCount} reseñas</span></div> : null}<p className="overview">{movie.overview}</p><WatchlistButton movie={movie} initial={movie.isInWatchlist} /></div>
        </section>
        <div className="movie-columns">
          <Surface as="form" className="review-editor" onSubmit={publish}>
            <div><h2>Escribe una reseña</h2><p>Puedes reseñar esta película todas las veces que quieras.</p></div>
            <RatingStars value={rating} onChange={setRating} size={34} />
            <textarea value={text} onChange={(event) => setText(event.target.value)} rows={7} maxLength={4000} placeholder="¿Qué te ha parecido?" />
            <label className="check-row"><input type="checkbox" checked={spoilers} onChange={(event) => setSpoilers(event.target.checked)} /><span>Contiene spoilers</span></label>
            {formError ? <p className="form-error">{formError}</p> : null}
            <button className="primary-button" disabled={working}><Send />{working ? "Publicando…" : "Publicar reseña"}</button>
          </Surface>
          <section className="movie-reviews">
            <div className="section-heading"><div><h2>Reseñas</h2><p>Las tuyas aparecen primero.</p></div><Segmented ariaLabel="Ordenar reseñas" value={sort} onChange={setSort} options={[{ value: "recent", label: "Recientes", icon: <Clock /> }, { value: "likes", label: "Likes", icon: <Star /> }]} /></div>
            {reviews.length ? <div className="review-list">{reviews.map((review) => <ReviewCard key={review.id} review={review} />)}</div> : <EmptyState icon={Film} title="Todavía no hay reseñas" text="La tuya puede ser la primera." />}
          </section>
        </div>
      </div>
    </div>
  );
}
